import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { switchMap, take, catchError, from, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SessionService } from './session.service';

/**
 * Interceptor funcional que agrega el token de Cognito a las peticiones al API Gateway.
 * Soporta tanto tokens de OIDC como tokens de login directo.
 * Renueva automáticamente tokens expirados usando el SessionService.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Solo interceptar llamadas a las API Gateway; un valor vacío haría startsWith('') verdadero
  const isApiCall = [
    environment.apiGateway,
    environment.salesApiGateway,
    environment.integrationsApiGateway,
    // La mesa de tickets de T.I. vive en su propia API (mismo user pool, mismo token)
    environment.itSupportApiGateway,
    // Supply Chain también tiene API propia (catálogo de inventario)
    environment.supplyChainApiGateway,
    // Cobranza estrena API propia con la facturación en cobranza (la compartida está en su tope)
    environment.collectionsApiGateway,
    // Lo transversal (catálogo de etiquetas) vive en TerraEnergyShared-Api
    environment.sharedApiGateway,
  ].some((base) => !!base && req.url.startsWith(base));
  if (!isApiCall) {
    return next(req);
  }

  const sessionService = inject(SessionService);

  // Priorizar sesión directa con Cognito SDK
  const sessionToken = sessionStorage.getItem('id_token');
  if (sessionToken) {
    return from(sessionService.getValidToken()).pipe(
      switchMap((validToken) => {
        if (!validToken) {
          // No se pudo obtener un token válido — dejar pasar sin auth
          return next(req);
        }
        const authReq = req.clone({
          setHeaders: { Authorization: `Bearer ${validToken}` },
        });
        return next(authReq).pipe(
          catchError((error: HttpErrorResponse) => {
            // Si recibimos 401, intentar renovar una vez más
            if (error.status === 401) {
              return from(sessionService.refreshSession()).pipe(
                switchMap((refreshedToken) => {
                  if (refreshedToken) {
                    const retryReq = req.clone({
                      setHeaders: { Authorization: `Bearer ${refreshedToken}` },
                    });
                    return next(retryReq);
                  }
                  sessionService.logout();
                  return throwError(() => error);
                }),
              );
            }
            return throwError(() => error);
          }),
        );
      }),
    );
  }

  // Fallback a token OIDC si existe
  const oidcService = inject(OidcSecurityService);

  return oidcService.getIdToken().pipe(
    take(1),
    switchMap((oidcToken) => {
      if (oidcToken) {
        const authReq = req.clone({
          setHeaders: { Authorization: `Bearer ${oidcToken}` },
        });
        return next(authReq);
      }
      return next(req);
    }),
  );
};
